/**
 * PartsOrderedTab — iter251
 * Supplier & Quantity report sourced from INVOICES: every invoice line
 * item that carries a supplier. Standard date-range filter + search, a
 * per-SUPPLIER aggregation and a detail listing (quotes excluded).
 * Data: GET /api/reports/parts-ordered.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Package, Search, X, ChevronLeft, ChevronRight, Calendar, Truck } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PartsOrderedTab({ getSiteParams }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ ...getSiteParams(), page: String(page), page_size: '50' });
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (search) params.append('search', search);
      const res = await fetch(`${BACKEND_URL}/api/reports/parts-ordered?${params}`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
      else toast.error('Failed to load parts ordered report');
    } catch (_e) {
      toast.error('Failed to load parts ordered report');
    } finally {
      setLoading(false);
    }
  }, [getSiteParams, dateFrom, dateTo, search, page]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const hasFilters = dateFrom || dateTo || search;
  const items = data?.items || [];
  const bySupplier = data?.by_supplier || [];

  return (
    <div className="space-y-6" data-testid="parts-ordered-tab">
      {/* Filters */}
      <Card className="p-4 bg-slate-50">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-700 flex items-center gap-2">
            <Calendar className="h-4 w-4" /> Invoice Date
          </h3>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); setSearch(''); setPage(1); }} data-testid="parts-clear-filters">
              <X className="h-4 w-4 mr-1" /> Clear All
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex gap-2">
            <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="flex-1" data-testid="parts-date-from" />
            <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="flex-1" data-testid="parts-date-to" />
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by part, supplier, customer, rego or invoice #..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-10"
              data-testid="parts-search"
            />
          </div>
        </div>
      </Card>

      {/* Totals strip */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500">Part Lines</p>
          <p className="text-2xl font-bold text-slate-900" data-testid="parts-total-rows">{(data?.total ?? 0).toLocaleString()}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500">Total Quantity</p>
          <p className="text-2xl font-bold text-emerald-700" data-testid="parts-total-qty">{(data?.total_qty ?? 0).toLocaleString()}</p>
        </Card>
      </div>

      {/* By-supplier aggregation */}
      <Card className="overflow-hidden">
        <div className="px-4 py-3 border-b bg-slate-50 flex items-center gap-2">
          <Truck className="h-4 w-4 text-slate-500" />
          <h3 className="font-semibold text-slate-700">Quantity by Supplier</h3>
        </div>
        <div className="overflow-x-auto max-h-72 overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Supplier</TableHead>
                <TableHead className="font-semibold text-right">Order Lines</TableHead>
                <TableHead className="font-semibold text-right">Total Qty</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bySupplier.length === 0 ? (
                <TableRow><TableCell colSpan={3} className="text-center py-8 text-slate-500">No supplier parts in this range</TableCell></TableRow>
              ) : bySupplier.map((s) => (
                <TableRow key={s.supplier} data-testid={`parts-supplier-row-${s.supplier}`}>
                  <TableCell className="max-w-[380px] truncate font-medium" title={s.supplier}>{s.supplier}</TableCell>
                  <TableCell className="text-right">{s.orders}</TableCell>
                  <TableCell className="text-right font-semibold">{s.total_qty}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Detail listing */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Date</TableHead>
                <TableHead className="font-semibold">Invoice #</TableHead>
                <TableHead className="font-semibold">Customer</TableHead>
                <TableHead className="font-semibold">Rego</TableHead>
                <TableHead className="font-semibold">Part</TableHead>
                <TableHead className="font-semibold">Supplier</TableHead>
                <TableHead className="font-semibold text-right">Qty</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
                </TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-500">
                  <Package className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                  No supplier parts found. Invoice line items with a supplier set appear here.
                </TableCell></TableRow>
              ) : items.map((r, i) => (
                <TableRow key={`${r.invoice_id}-${i}`} className="hover:bg-slate-50">
                  <TableCell>{r.date || '-'}</TableCell>
                  <TableCell>
                    <button
                      onClick={() => navigate(`/invoices/${r.invoice_id}`)}
                      className="text-blue-600 hover:underline font-medium"
                      data-testid={`parts-inv-link-${r.invoice_id}`}
                    >
                      {r.doc_no || '-'}
                    </button>
                  </TableCell>
                  <TableCell className="max-w-[160px] truncate" title={r.customer_name}>{r.customer_name || '-'}</TableCell>
                  <TableCell className="font-mono text-sm">{r.vehicle_rego || '-'}</TableCell>
                  <TableCell className="max-w-[280px] truncate" title={r.part}>{r.part}</TableCell>
                  <TableCell>{r.supplier || '-'}</TableCell>
                  <TableCell className="text-right font-semibold">{r.qty}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
          <span className="text-sm text-slate-600">
            {data?.total ? `${data.total.toLocaleString()} lines · page ${data.page} of ${data.total_pages}` : 'No records'}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} data-testid="parts-prev-page">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(data?.total_pages || 1, p + 1))} disabled={page >= (data?.total_pages || 1)} data-testid="parts-next-page">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
